"use client";

import { c as _c } from "react/compiler-runtime";
import { SlidersHorizontal } from "lucide-react";
import { usePathname, useSearchParams } from "next/navigation";
import { type ReactNode, Suspense, useEffect, useState } from "react";
import { FiltersSheet, getActiveFilterCount } from "~/app/(site)/search/_components/FiltersSheet";
import { MobileSearchSheet } from "~/app/(site)/search/_components/MobileSearchSheet";
import { SearchNavPill } from "~/app/(site)/search/_components/SearchNavPill";
import { Logo } from "~/components/ui/Logo";
import { isProtectedPath } from "~/lib/auth/protected-routes";
import { getPublicNavItems } from "~/lib/constants/nav";
import { cn } from "~/lib/utils";
import { formatShort } from "~/lib/utils/datetime";
import { useNavAuthStore } from "~/store/navAuthStore";
import { useSearchUIStore } from "~/store/searchUIStore";
import { AuthedActionsPlaceholder } from "./AuthedActionsPlaceholder";
import { isNavPillActive, NavPill } from "./NavPill";
import { SignedOutActions } from "./SignedOutActions";
import { useNavAuthSync } from "./useNavAuth";

/**
 * Everything in the navbar that depends only on the pathname: the header and
 * its shape, the logo, the public links, and the search-page chrome. No awaits
 * anywhere in this tree, so it prerenders into the static shell and the public
 * links are in the HTML for crawlers.
 *
 * The three auth-dependent regions arrive as slots, each wrapped here in its
 * own Suspense boundary — Cache Components does not accept a boundary declared
 * in a client component's props, so it has to be this side of the seam.
 * `sidebar` is a slot rather than something this component renders because the
 * panel is `position: fixed` and the header sets `backdrop-blur-md`, which
 * would make the header its containing block.
 */
export function NavBarShell(t0) {
  const $ = _c(54);
  const {
    navExtra,
    actions,
    sidebar
  } = t0;
  const pathname = usePathname();
  useNavAuthSync();
  const sidebarOpen = useNavAuthStore(_temp);
  const setSidebarOpen = useNavAuthStore(_temp2);
  const mobileSearchOpen = useSearchUIStore(_temp3);
  const setMobileSearchOpen = useSearchUIStore(_temp4);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  let t1;
  let t2;
  if ($[0] !== setSidebarOpen) {
    t1 = () => {
      const lgMq = window.matchMedia("(min-width: 1024px)");
      setIsDesktop(lgMq.matches);
      const lgHandler = e => setIsDesktop(e.matches);
      lgMq.addEventListener("change", lgHandler);
      const mq = window.matchMedia("(min-width: 768px)");
      const handler = e_0 => e_0.matches && setSidebarOpen(false);
      mq.addEventListener("change", handler);
      return () => {
        lgMq.removeEventListener("change", lgHandler);
        mq.removeEventListener("change", handler);
      };
    };
    t2 = [setSidebarOpen];
    $[0] = setSidebarOpen;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let actionsFallback;
  let isSearchPage;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[3] !== isDesktop || $[4] !== navExtra || $[5] !== pathname) {
    isSearchPage = pathname.startsWith("/search");
    const publicNavItems = getPublicNavItems();
    let t8;
    if ($[13] !== pathname) {
      t8 = isProtectedPath(pathname) ? <AuthedActionsPlaceholder /> : <SignedOutActions />;
      $[13] = pathname;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    actionsFallback = t8;
    const t9 = isSearchPage ? "flex flex-col" : "flex items-center h-16 px-6 md:px-8";
    if ($[15] !== t9) {
      t7 = cn("sticky top-0 z-50 bg-white/96 backdrop-blur-md", "border-b border-(--ink-10) [transition:border-color_var(--dur-fast)_var(--ease-snap)]", t9);
      $[15] = t9;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    t4 = cn("flex w-full items-center", isSearchPage && "px-4 md:px-6 h-14 lg:h-[72px]");
    let t10;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <Logo href="/" className="shrink-0" />;
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    const t11 = !isSearchPage && <><span className="hidden h-5 w-px bg-(--ink-20) md:block ml-5" aria-hidden={true} /><nav className="hidden items-center gap-0.5 md:flex ml-1">{publicNavItems.map(item => <NavPill key={item.href} href={item.href} label={item.label} active={isNavPillActive(pathname, item.href)} />)}<Suspense fallback={null}>{navExtra}</Suspense></nav></>;
    if ($[18] !== t11) {
      t5 = <div className="flex shrink-0 items-center">{t10}{t11}</div>;
      $[18] = t11;
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    t6 = isSearchPage && <div className="relative hidden flex-1 items-center justify-center lg:flex"><Suspense fallback={<SearchNavPillSkeleton />}><SearchNavPill onFiltersOpen={() => setFiltersOpen(true)} isVisible={isDesktop} /></Suspense></div>;
    t3 = cn("flex-1", isSearchPage && "lg:hidden");
    $[3] = isDesktop;
    $[4] = navExtra;
    $[5] = pathname;
    $[6] = actionsFallback;
    $[7] = isSearchPage;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    actionsFallback = $[6];
    isSearchPage = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
    t7 = $[12];
  }
  let t8;
  if ($[20] !== t3) {
    t8 = <div className={t3} />;
    $[20] = t3;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== actions || $[23] !== actionsFallback) {
    t9 = <Suspense fallback={actionsFallback}>{actions}</Suspense>;
    $[22] = actions;
    $[23] = actionsFallback;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t4 || $[26] !== t5 || $[27] !== t6 || $[28] !== t8 || $[29] !== t9) {
    t10 = <div className={t4}>{t5}{t6}{t8}{t9}</div>;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== isDesktop || $[32] !== isSearchPage || $[33] !== setMobileSearchOpen || $[34] !== sidebarOpen) {
    t11 = isSearchPage && <div className={cn("animate-slide-in-down flex items-center gap-2 px-4 md:px-6 lg:hidden h-[60px] shrink-0", "transition-[max-height,padding,opacity] duration-200 ease-in-out", sidebarOpen ? "max-h-0 overflow-hidden pb-0 opacity-0 pointer-events-none" : "max-h-[72px] overflow-visible pb-3 opacity-100")}><div className="flex flex-1 items-center gap-2 md:hidden"><Suspense fallback={<MobileSearchPillSkeleton />}><MobileSearchPillInner onSearchOpen={() => setMobileSearchOpen(true)} onFiltersOpen={() => setFiltersOpen(true)} /></Suspense></div><div className="hidden flex-1 items-center justify-center md:flex"><Suspense fallback={<SearchNavPillSkeleton />}><SearchNavPill onFiltersOpen={() => setFiltersOpen(true)} isVisible={!isDesktop} /></Suspense></div></div>;
    $[31] = isDesktop;
    $[32] = isSearchPage;
    $[33] = setMobileSearchOpen;
    $[34] = sidebarOpen;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== t10 || $[37] !== t11 || $[38] !== t7) {
    t12 = <header className={t7}>{t10}{t11}</header>;
    $[36] = t10;
    $[37] = t11;
    $[38] = t7;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== sidebar) {
    t13 = <Suspense fallback={null}>{sidebar}</Suspense>;
    $[40] = sidebar;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== filtersOpen || $[43] !== isSearchPage) {
    t14 = isSearchPage && <Suspense fallback={null}><FiltersSheet open={filtersOpen} onClose={() => setFiltersOpen(false)} /></Suspense>;
    $[42] = filtersOpen;
    $[43] = isSearchPage;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== isSearchPage || $[46] !== mobileSearchOpen || $[47] !== setMobileSearchOpen) {
    t15 = isSearchPage && <Suspense fallback={null}><MobileSearchSheet open={mobileSearchOpen} onClose={() => setMobileSearchOpen(false)} /></Suspense>;
    $[45] = isSearchPage;
    $[46] = mobileSearchOpen;
    $[47] = setMobileSearchOpen;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  let t16;
  if ($[49] !== t12 || $[50] !== t13 || $[51] !== t14 || $[52] !== t15) {
    t16 = <>{t12}{t13}{t14}{t15}</>;
    $[49] = t12;
    $[50] = t13;
    $[51] = t14;
    $[52] = t15;
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  return t16;
}

// ── Skeleton ───────────────────────────────────────────────────────────────
function _temp4(s_2) {
  return s_2.setMobileSearchOpen;
}
function _temp3(s_1) {
  return s_1.mobileSearchOpen;
}
function _temp2(s_0) {
  return s_0.setSidebarOpen;
}
function _temp(s) {
  return s.sidebarOpen;
}
function SearchNavPillSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="h-[52px] w-[520px] animate-pulse rounded-full border border-(--ink-20) bg-(--ink-05)" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function MobileSearchPillSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex flex-1 items-center gap-2"><div className="h-2xl flex-1 animate-pulse rounded-full border border-(--ink-20) bg-(--ink-05)" /><div className="h-9 w-9 shrink-0 animate-pulse rounded-full border border-(--ink-20) bg-(--ink-05)" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

// ── Mobile search pill (needs useSearchParams — rendered inside Suspense) ──

function MobileSearchPillInner(t0) {
  const $ = _c(30);
  const {
    onSearchOpen,
    onFiltersOpen
  } = t0;
  const rawParams = useSearchParams();
  const activeFilterCount = getActiveFilterCount(rawParams);
  let VEHICLE_LABELS;
  let locationLine;
  let startLabel;
  let t1;
  let vehicleSize;
  if ($[0] !== rawParams) {
    const q = rawParams.get("q") ?? "";
    const startsAt = rawParams.get("starts_at") ?? "";
    const endsAt = rawParams.get("ends_at") ?? "";
    vehicleSize = rawParams.get("vehicle_size") ?? "";
    locationLine = q ? q.split(",")[0]?.trim() ?? q : "";
    let t2;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        motorcycle: "Motorcycle",
        subcompact: "Subcompact",
        compact: "Compact",
        standard: "Standard",
        suv: "SUV",
        truck: "Truck",
        oversized: "Oversized"
      };
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    VEHICLE_LABELS = t2;
    startLabel = startsAt ? formatShort(startsAt) : "";
    t1 = endsAt ? formatShort(endsAt) : "";
    $[0] = rawParams;
    $[1] = VEHICLE_LABELS;
    $[2] = locationLine;
    $[3] = startLabel;
    $[4] = t1;
    $[5] = vehicleSize;
  } else {
    VEHICLE_LABELS = $[1];
    locationLine = $[2];
    startLabel = $[3];
    t1 = $[4];
    vehicleSize = $[5];
  }
  const endLabel = t1;
  const dateRange = startLabel && endLabel && startLabel !== endLabel ? `${startLabel} – ${endLabel}` : startLabel || endLabel;
  const vehicleLabel = vehicleSize ? VEHICLE_LABELS[vehicleSize] ?? "" : "";
  let t2;
  if ($[7] !== dateRange || $[8] !== vehicleLabel) {
    t2 = [dateRange, vehicleLabel].filter(Boolean);
    $[7] = dateRange;
    $[8] = vehicleLabel;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const subLine = t2.join(" \xB7 ");
  const t3 = locationLine || "Search parking";
  let t4;
  if ($[10] !== t3) {
    t4 = <span className="w-full truncate text-center text-[13px] font-semibold leading-tight text-(--ink)">{t3}</span>;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = subLine || "Add dates \xB7 Select vehicle";
  let t6;
  if ($[12] !== t5) {
    t6 = <span className="w-full truncate text-center text-[11px] leading-tight text-(--ink-60)">{t5}</span>;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== onSearchOpen || $[15] !== t4 || $[16] !== t6) {
    t7 = <button type="button" onClick={onSearchOpen} className="flex min-w-0 h-2xl flex-1 flex-col items-center justify-center gap-0.5 rounded-full border border-(--ink-20) bg-white px-4 py-2.5 shadow-sm transition-shadow hover:shadow-md active:shadow-sm">{t4}{t6}</button>;
    $[14] = onSearchOpen;
    $[15] = t4;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const t8 = activeFilterCount > 0 ? "border-(--blue) bg-(--blue-pale) text-(--blue)" : "border-(--ink-20) bg-white text-(--ink-60) hover:bg-(--ink-05)";
  let t9;
  if ($[18] !== t8) {
    t9 = cn("relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-colors", t8);
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <SlidersHorizontal className="h-4 w-4" strokeWidth={2.2} />;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== activeFilterCount) {
    t11 = activeFilterCount > 0 && <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-(--blue) text-[9px] font-bold text-white">{activeFilterCount}</span>;
    $[21] = activeFilterCount;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== onFiltersOpen || $[24] !== t11 || $[25] !== t9) {
    t12 = <button type="button" onClick={onFiltersOpen} className={t9} aria-label="Filters">{t10}{t11}</button>;
    $[23] = onFiltersOpen;
    $[24] = t11;
    $[25] = t9;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12 || $[28] !== t7) {
    t13 = <>{t7}{t12}</>;
    $[27] = t12;
    $[28] = t7;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
}